import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api';

interface MeterEntry {
  id: number;
  productionId: number;
  machineId: number;
  employeeId: number;
  meters: number;
  shiftType: 'A' | 'B';
  entryDate: string;
  notes?: string;
  isEditable: boolean;
  machine?: {
    id: number;
    number: string;
    group?: {
      id: number;
      name: string;
    };
  };
  employee?: {
    id: number;
    user?: {
      name: string;
    };
  };
  createdAt: string;
}

interface Production {
  id: number;
  status: string;
  meters: number;
  metersProduced: number;
  machine?: {
    number: string;
  };
  beam?: {
    number: string;
    totalMeters: number;
  };
}

interface Machine {
  id: number;
  number: string;
}

interface Employee {
  id: number;
  user?: {
    name: string;
  };
}

const MeterEntries: React.FC = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<MeterEntry | null>(null);
  const [selectedProduction, setSelectedProduction] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    productionId: '',
    machineId: '',
    employeeId: '',
    meters: '',
    shiftType: 'A' as 'A' | 'B',
    entryDate: new Date().toISOString().split('T')[0],
    notes: ''
  });

  const queryClient = useQueryClient();

  // Fetch active productions
  const { data: productions } = useQuery<Production[]>({
    queryKey: ['productions'],
    queryFn: async () => {
      const data = await apiService.get('/production');
      return data.productions || [];
    }
  });

  // Fetch machines
  const { data: machines } = useQuery<Machine[]>({
    queryKey: ['machines'],
    queryFn: async () => {
      const data = await apiService.get('/machines');
      return data.machines || [];
    }
  });

  // Fetch employees
  const { data: employees } = useQuery<Employee[]>({
    queryKey: ['employees'],
    queryFn: async () => {
      const data = await apiService.get('/employees');
      return data.employees || [];
    }
  });

  // Fetch meter entries for selected production
  const { data: meterEntries, isLoading } = useQuery<MeterEntry[]>({
    queryKey: ['meterEntries', selectedProduction],
    queryFn: async () => {
      if (!selectedProduction) return [];
      const data = await apiService.get(`/meter-entries/production/${selectedProduction}`);
      return data.meterEntries || [];
    },
    enabled: !!selectedProduction
  });

  // Add meter entry mutation
  const addMeterEntry = useMutation({
    mutationFn: async (data: any) => {
      return await apiService.post('/meter-entries', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meterEntries'] });
      queryClient.invalidateQueries({ queryKey: ['productions'] });
      setShowAddModal(false);
      resetForm();
      alert('Meter entry added successfully!');
    },
    onError: (error: any) => {
      alert(error.response?.data?.message || 'Failed to add meter entry');
    }
  });

  // Update meter entry mutation
  const updateMeterEntry = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      return await apiService.put(`/meter-entries/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meterEntries'] });
      queryClient.invalidateQueries({ queryKey: ['productions'] });
      setShowEditModal(false);
      setSelectedEntry(null);
      alert('Meter entry updated successfully!');
    },
    onError: (error: any) => {
      alert(error.response?.data?.message || 'Failed to update meter entry');
    }
  });

  // Delete meter entry mutation
  const deleteMeterEntry = useMutation({
    mutationFn: async (id: number) => {
      return await apiService.delete(`/meter-entries/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meterEntries'] });
      queryClient.invalidateQueries({ queryKey: ['productions'] });
      alert('Meter entry deleted successfully!');
    },
    onError: (error: any) => {
      alert(error.response?.data?.message || 'Failed to delete meter entry');
    }
  });

  const resetForm = () => {
    setFormData({
      productionId: '',
      machineId: '',
      employeeId: '',
      meters: '',
      shiftType: 'A',
      entryDate: new Date().toISOString().split('T')[0],
      notes: ''
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    addMeterEntry.mutate({
      ...formData,
      productionId: parseInt(formData.productionId),
      machineId: parseInt(formData.machineId),
      employeeId: parseInt(formData.employeeId),
      meters: parseFloat(formData.meters)
    });
  };

  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEntry) return;
    
    updateMeterEntry.mutate({
      id: selectedEntry.id,
      data: {
        meters: parseFloat(formData.meters),
        notes: formData.notes
      }
    });
  };

  const handleEdit = (entry: MeterEntry) => {
    setSelectedEntry(entry);
    setFormData({
      productionId: entry.productionId.toString(),
      machineId: entry.machineId.toString(),
      employeeId: entry.employeeId.toString(),
      meters: entry.meters.toString(),
      shiftType: entry.shiftType,
      entryDate: entry.entryDate,
      notes: entry.notes || ''
    });
    setShowEditModal(true);
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to delete this meter entry?')) {
      deleteMeterEntry.mutate(id);
    }
  };

  const activeProductions = productions?.filter((p: Production) => p.status === 'active') || [];
  const totalMeters = meterEntries?.reduce((sum: number, entry: MeterEntry) => sum + entry.meters, 0) || 0;
  const selectedProd = productions?.find((p: Production) => p.id === selectedProduction);

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Meter Entries</h1>
        <p className="text-gray-600">Manage meter production entries per employee and shift</p>
      </div>

      {/* Production Selector */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Select Production</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Active Production
            </label>
            <select
              value={selectedProduction || ''}
              onChange={(e) => setSelectedProduction(e.target.value ? parseInt(e.target.value) : null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="">-- Select Production --</option>
              {activeProductions.map((prod: Production) => (
                <option key={prod.id} value={prod.id}>
                  Production #{prod.id} - Machine {prod.machine?.number} - Beam {prod.beam?.number}
                </option>
              ))}
            </select>
          </div>
          {selectedProd && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-sm">
                <p><strong>Expected Meters:</strong> {(selectedProd.meters || 0).toFixed(2)}</p>
                <p><strong>Meters Produced:</strong> {(selectedProd.metersProduced || 0).toFixed(2)}</p>
                <p><strong>Remaining:</strong> {((selectedProd.meters || 0) - (selectedProd.metersProduced || 0)).toFixed(2)}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {selectedProduction && (
        <>
          {/* Add Button */}
          <div className="mb-4 flex justify-between items-center">
            <div>
              <span className="text-lg font-semibold">Total Meters Logged: {totalMeters.toFixed(2)}</span>
            </div>
            <button
              onClick={() => {
                setFormData({ ...formData, productionId: selectedProduction.toString() });
                setShowAddModal(true);
              }}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              + Add Meter Entry
            </button>
          </div>

          {/* Meter Entries Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {isLoading ? (
              <div className="p-8 text-center text-gray-500">Loading meter entries...</div>
            ) : meterEntries && meterEntries.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Machine</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Meters</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {meterEntries.map((entry: MeterEntry) => (
                    <tr key={entry.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">{entry.entryDate}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">{entry.employee?.user?.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {entry.machine?.number}
                        {entry.machine?.group && <span className="text-gray-500 text-xs ml-1">({entry.machine.group.name})</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`px-2 py-1 rounded ${entry.shiftType === 'A' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}`}>
                          {entry.shiftType === 'A' ? '☀️ Shift A' : '🌙 Shift B'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold">{entry.meters.toFixed(2)}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{entry.notes || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {entry.isEditable ? (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEdit(entry)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(entry.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Delete
                            </button>
                          </div>
                        ) : (
                          <span className="text-gray-400">Locked</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-8 text-center text-gray-500">No meter entries found for this production</div>
            )}
          </div>
        </>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-2xl font-bold mb-4">Add Meter Entry</h2>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Machine</label>
                  <select
                    value={formData.machineId}
                    onChange={(e) => setFormData({ ...formData, machineId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  >
                    <option value="">-- Select Machine --</option>
                    {machines?.map((machine: Machine) => (
                      <option key={machine.id} value={machine.id}>
                        Machine {machine.number}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
                  <select
                    value={formData.employeeId}
                    onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  >
                    <option value="">-- Select Employee --</option>
                    {employees?.map((employee: Employee) => (
                      <option key={employee.id} value={employee.id}>
                        {employee.user?.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meters</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.meters}
                    onChange={(e) => setFormData({ ...formData, meters: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Shift</label>
                  <select
                    value={formData.shiftType}
                    onChange={(e) => setFormData({ ...formData, shiftType: e.target.value as 'A' | 'B' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  >
                    <option value="A">☀️ Shift A (8 AM - 8 PM)</option>
                    <option value="B">🌙 Shift B (8 PM - 8 AM)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Entry Date</label>
                  <input
                    type="date"
                    value={formData.entryDate}
                    onChange={(e) => setFormData({ ...formData, entryDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    rows={3}
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addMeterEntry.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {addMeterEntry.isPending ? 'Adding...' : 'Add Entry'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-2xl font-bold mb-4">Edit Meter Entry</h2>
            <form onSubmit={handleUpdate}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meters</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.meters}
                    onChange={(e) => setFormData({ ...formData, meters: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    rows={3}
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedEntry(null);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateMeterEntry.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {updateMeterEntry.isPending ? 'Updating...' : 'Update Entry'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeterEntries;

